import { FC, useState } from 'react';
import { Center, InputRightElement } from '@chakra-ui/react';

import {
  isFetchingMore,
  isLoading,
  isSettingVariables,
} from 'common/graphql/networkStatus';
import useDebounce from 'common/hooks/useDebounce';
import Button from 'components/_core/Button';
import IconButton from 'components/_core/IconButton';
import SunshineLoader from 'components/_core/SunshineLoader';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import SearchBar from 'components/SearchBar';
import { logEvent } from 'features/Analytics/analytics';
import {
  CatalogItem,
  useCatalogItemsQuery,
} from 'features/Work/hooks/useCatalogItemsQuery';

import List from './components/List';
import locales from './locales';

interface CatalogItemsListModalProps
  extends Omit<SunshineModalProps, 'children'> {
  onSave: (catalogItems: CatalogItem[]) => void;
}

const CatalogItemsListModal: FC<CatalogItemsListModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());

  const { catalogItems, fetchMore, networkStatus, pageInfo } =
    useCatalogItemsQuery(
      debouncedSearchTerm
        ? { filters: { searchInput: debouncedSearchTerm } }
        : {},
    );

  const loading = isLoading(networkStatus) || isSettingVariables(networkStatus);

  const handleSave = () => {
    const idToCatalogItemMap = new Map<string, CatalogItem>(
      catalogItems.map((item) => [item.invoiceCatalogItemId, item]),
    );

    const selectedCatalogItems = selectedItems
      .map((itemId) => idToCatalogItemMap.get(itemId))
      .filter((item): item is CatalogItem => !!item);

    if (selectedCatalogItems.length > 0) {
      onSave(selectedCatalogItems);
      logEvent({
        name: 'Catalog Item Clicked',
        properties: { source: 'web-app' },
        usePrefix: false,
      });
      onClose();
    }
  };

  const clearSearch = () => setSearchTerm('');

  return (
    <SunshineModal
      contentProps={{ height: 'full' }}
      isOpen={isOpen}
      onClose={onClose}
      returnFocusOnClose={false}
    >
      <SunshineModal.Header minHeight="fit-content" />

      <SunshineModal.Body
        css={{ paddingBottom: 0 }}
        display="flex"
        flexDirection="column"
      >
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <SearchBar
          marginBottom="space-16"
          onChange={setSearchTerm}
          placeholder={locales.searchPlaceholder}
          value={searchTerm}
        >
          {searchTerm ? (
            <InputRightElement>
              <IconButton
                aria-label={locales.clear}
                icon="cross"
                onClick={clearSearch}
                variant="inline-secondary"
              />
            </InputRightElement>
          ) : null}
        </SearchBar>

        {loading ? (
          <Center flexGrow={1}>
            <SunshineLoader />
          </Center>
        ) : (
          <List
            catalogItems={catalogItems}
            isFetchingMore={isFetchingMore(networkStatus)}
            onClearSearch={clearSearch}
            onFetchMore={fetchMore}
            onSelectItems={setSelectedItems}
            pageInfo={pageInfo}
            selectedItems={selectedItems}
          />
        )}
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          icon="arrow-right"
          isDisabled={selectedItems.length === 0}
          onClick={handleSave}
        >
          {locales.save}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default CatalogItemsListModal;
